import type { EntityTypes } from '$lib/core/map/map-utils';
import * as THREE from 'three';

// eslint-disable-next-line @typescript-eslint/no-namespace
/**
 * Cables are grouped in the following hierarchy:
 * 1. Cable path level 1. This is the entity which is available and visible above ground. The entity is rendered by a minimal czml dataset, or rendered after a new cable path is drawn.
 * 2. Trench / Cables level 2. The trench and cable entities are rendered and available when a cable path is selected and enriched with data from the server. This allows us to visualize 
 *    the contained entities belonging to the path. Entities within the trench is also tied to the trench-entity for a more logical grouping and easier access.
 */

export const MARKER_FUNCTION = {
    BESKYTTELSE: 'BESKYTTELSE',
    ADVARSEL: 'ADVARSEL'
}

export const OBJECT_TYPES = {
	DEFAULT: 'DEFAULT',
	// Kabels
	KABEL_LS: 'KABEL_LS',
	KABEL_HS: 'KABEL_HS',
	JORD: 'JORD',
	// Pipes
	ROR: 'ROR',
} as const;

export declare namespace ICableModel {

	// The data object which exists on a cable entity
	export interface Cable {
		operationalMarking: string;
		cableConductorData: CableConductorData; // The cable specifications
		placement: Partial<Grid2DPlacement>;
		markingData?: MarkingBaseData, // Optional marking / protection data
	}

	export interface EccCable {
		operationalMarking: string;
		cableConductorData: EccBaseData; // The cable specifications
		placement: Partial<Grid2DPlacement>;
		markingData?: MarkingBaseData, // Optional marking / protection data
	}

	// Ecc / jording data
	export interface EccBaseData {
		id: number;
        displayName: string,
        diameter: number, // in mm
        trenchObjectType: "jordtråd"
	}
	// The data object which exists on each cable type. Fetched from cablebasedataquery, similar to lines
	export interface CableConductorData { 
		id: number,
        displayName: string,
        voltageLevel: "LS" | "HS",
        diameter: number, // in mm
        trenchObjectType: "kabel"
       
	}
	// Cable entity properties (PropertyBagModel)
	export interface CablePropertyBagModel {
		entityType: typeof EntityTypes['POLYLINE_VOLUME'];
		data: Cable;
	}
	export interface CablePathPropertyBagModel {
		entityType: typeof EntityTypes['CABLE_PATH'];
		data: TrenchData;
	}

	/** A dynamic type which primarily represents a trench object */
	export interface TrenchObject {
		displayName: string;
		type: keyof typeof OBJECT_TYPES; // e.g. 'KABEL_LS', 'ROR'
		data: TrenchObjectTypes;
		childObjects: TrenchObject[];
	}
	/** Objects that can exist in a trench */
	type TrenchObjectTypes =  Cable | PipeData | EccCable // | MarkingData;

	/** Connected to a trench object. It does not exist on its own without a related trench object. It is however a separate figure in the 2D Grid.*/
	export interface MarkingBaseData {
		id?: number; // Internal ID
		displayName: string; // Readable name of the marking 
		width: number; // Thickness in mm
		height: number; // Height in mm
		markerFunction: string; // 
		//placement: Grid2DPlacement; // Placement in the trench
	}

	export interface PipeBaseData {
		id: number;
        displayName: string,
        diameter: number, // in mm
        trenchObjectType: "rør",
        borderWidth: number
	}

	export interface PipeData {
		id?: number, // Internal ID
		operationalMarking: string; // Human readable identifier for the pipe
		placement: Partial<Grid2DPlacement>;
		markingData?: MarkingBaseData, // Optional marking / protection data
		pipeBaseData: PipeBaseData
	}

	export interface Grid2DPlacement {
		placementLogic: 'minimumDistance'; // e.g. 'minimumDistance'. Decides how new entities are placed in the trench
		minimalOfffsetRequirement: number; // in mm
		offsetX: number; // in mm. 
		offsetY: number; // in mm.
	}
	
	export interface TrenchData {
		description: string;
		horizontalPadding: number; // Avstand venstre og høyre mellom kabler og bunn av grøft. Bestemmer bredden i bunn. (Avstand til kantlinjer)
		topCover: never[]; // Todo: Add later
        refillZones: TrenchFillZone[];
		cableZones: {
			objects: TrenchObject[];
			margin: number;
			zone: Omit<TrenchFillZone, 'depth'>, // Cable zones define the depth from "margin" and the biggest diameter of an object in the zone
		}[];
		curve: THREE.Curve<THREE.Vector3> | undefined;
		steps: number;
        foundationZones: TrenchFillZone[];
		trenchAngle: number; // Vinkel på grøft i topp (Grøftevinkel)
		trenchParentId: string;
		startEntity: string; // Entity ID for start point
		endEntity: string; // Entity ID for end point
	}

	export interface TrenchFillZone {
		name: string,
		fillId: number | undefined, // Referanse til fyllmasse. Skal vise informasjon dersom populert
		depth: number,	 // Høyde på sonen i meter
	}
}
